import ButtonChannel from "../models/ButtonChannel";
import { getSignalTypeBundle } from "../models/SignalBundles";
import AddressGeneratorUtils from "./AddressGeneratorUtils";
import Logger from "../Logger";
import Multiplexer from "../models/Multiplexer";
import Consumer from "../models/Consumer";
import { sortArrayByProperty } from "../models/utils";
import GroupAddress from "../models/GroupAdress";
import type Button from "../models/Button";
import Device from "../models/Device";
import SignalTypes from "../models/SignalTypes";
import { linkActor, linkActorChannel } from "./linkActors";

const logger = new Logger();

//
// returns true if there is any change in GroupAdress-Assignment
//
export function linkButton(button: Button) {
  if (button.template == undefined) {
    //
    // Button has not a product assigen yet
    // so we cant create any group address assignments
    //
    return;
  }

  button.buttonChannels.forEach((buttonChannel) => {
    buttonChannel.assetIsRequestChannel();

    if (buttonChannel.isLogicInputChannel) {
      linkActorChannel(buttonChannel);
    } else if (buttonChannel.isLogicOutputChannel) {
      linkButtonSingle(buttonChannel);
    } else if (buttonChannel.isStatusChannel) {
      linkButtonSingle(buttonChannel);
    } else if (buttonChannel.isActorChannel) {
      linkActorChannel(buttonChannel);
    } else {
      const isGroupButtonChannel = buttonChannel.connections.size > 1;
      if (isGroupButtonChannel) {
        //
        // mwe 3.11.2024 (Sardinien)
        //
        // die änderungen hier betreffen "aussenstehende Aktoren"
        // somit wird hier das "changed-Prinzip" bezogen auf den "button" durchbrochen
        //
        // ggfls sollten wir noch schauen ob wir das "umdrehen"
        // dh jeder Actor sucht quasi alle Buttons ab od die eine Gruppe haben die mit dem Aktor verbunden ist
        //
        //

        linkGroupButtonActions(buttonChannel);
        linkGroupButtonStatuses(buttonChannel);
      } else {
        linkButtonSingle(buttonChannel);
      }
    }
  });
}

export function linkButtons(restrictToDivicesGlobal: Array<Device> | undefined) {
  state.floors.forEach((floor) => {
    floor.rooms.forEach((room) => {
      room.buttons.forEach((button) => {
        if (!restrictToDivicesGlobal || restrictToDivicesGlobal.includes(button)) {
          linkButton(button);
        }
      });
    });
  });
}

function linkGroupButtonActions(buttonChannel: ButtonChannel) {
  logger.log(`linkGroupButtonToAllActorChannels ${buttonChannel.fullTag} ${buttonChannel.name}`);

  if (buttonChannel.signals.size == 0) {
    logger.error(`linkGroupButtonToAllActorChannels ButtonChannel has no Signals: ${buttonChannel.name}`);
    return false;
  }

  buttonChannel.getConnectedActorChannels().forEach((actorChannel) => {
    buttonChannel.signals.forEach((buttonSignal) => {
      const actorSignal = actorChannel?.getOrCreateSignalByType(buttonSignal.type);
      const operationModeTypes = [
        SignalTypes.OperationMode.name,
        SignalTypes.OperationMode_Comfort.name,
        SignalTypes.OperationMode_Eco.name,
        SignalTypes.OperationMode_FrostProtection.name,
        SignalTypes.OperationMode_HeatProtection.name,
        SignalTypes.OperationMode_Night.name,
      ];

      let isGlobalGroupAdress = buttonSignal.type.globalGroupAdress != undefined;
      if (operationModeTypes.includes(buttonSignal.type.name)) {
        //
        // für HAVC OperationMode gibt es 2 Möglichkeiten
        // 1. Der Button steuert den globalen HVAC OperationMode "31/1/20"
        //    das ist der Fall wenn der Button mit keiner Heating Funtion verbunden ist
        //
        // 2. Der Button steuert den HVAC OperationMode des angeschlosssen HeatingActorChannels
        //    in diesem Fall setzten wir hier "isGlobalGroupAdress" auf false
        //
        if (actorSignal) {
          isGlobalGroupAdress = false;
        }
      }

      if (!isGlobalGroupAdress && !buttonSignal.type.isStatus) {
        //
        // Für alle Signale des ButtonChannel
        //
        if (!actorSignal) {
          logger.log(
            `linkGroupButtonToAllActorChannels no actorKnxSignal for:  ${buttonSignal.fullTag} ${buttonSignal.type.name}`,
          );
        } else {
          const groupAddressFromButton = buttonSignal.groupAddresses.firstItem;
          if (!groupAddressFromButton) {
            logger.devError(
              `linkGroupButtonToAllActorChannels ${buttonChannel.fullTag}:  mising groupAddress for ButtonChannel ${buttonChannel.fullTag} Signal: ${buttonSignal.type.name}`,
            );
          } else {
            //
            // for groups we can have only ONE StatusSource == ONE ActorChannel
            //

            logger.log(
              `linkGroupButtonToAllActorChannels link actorSignal (status):  ${actorSignal.fullTag} <- ${groupAddressFromButton.fullAddress} ${groupAddressFromButton.name} `,
            );

            let skip = false;
            if (actorSignal.type.isStatus) {
              //
              // check if the status-actorSignal allready has a (manual) GroupAddress
              // if so, we cant create / add a new one,
              // because only 1 GA for outgoing comobjects is valid
              //
              if (actorSignal.groupAddresses.length > 0) {
                skip = true;
              }
            }

            if (!skip) {
              actorSignal.addGroupAddress(groupAddressFromButton);
            }
          }
        }
      }
    });
  });
}

function linkButtonSingle(buttonChannel: ButtonChannel) {
  try {
    if (buttonChannel.className != "ButtonChannel") {
      logger.error(`linkButtonSingle: (className != "ButtonChannel) ${buttonChannel.fullTag} ${buttonChannel.name}`);
    }

    logger.log(`linkButtonSingle ${buttonChannel.fullTag} ${buttonChannel.name}`);

    let consumerOrMultiplexer: Consumer | Multiplexer | undefined = buttonChannel.getConnectedConsumer();

    if (!consumerOrMultiplexer && buttonChannel.className == "ButtonChannel") {
      consumerOrMultiplexer = buttonChannel.getConnectedMultiplexer();
    }

    if (consumerOrMultiplexer) {
      logger.log(
        `linkButtonSingle  consumerOrMultiplexer=${consumerOrMultiplexer.fullTag} ${consumerOrMultiplexer.name}`,
      );

      let signalTypes = getSignalTypeBundle(buttonChannel.channelType);
      for (const signalFromButton of buttonChannel.signals){
        let signalTypeFromButtonSignal= signalFromButton.type;
        if(!signalTypes.includes(signalTypeFromButtonSignal)){
          signalTypes.push(signalTypeFromButtonSignal)
        }
      }
      signalTypes.forEach((signalType) => {
        let groupAdress: GroupAddress | undefined;

        let isGlobalGroupAdress = signalType.globalGroupAdress != undefined;

        const operationModeTypes = [
          SignalTypes.OperationMode.name,
          SignalTypes.OperationMode_Comfort.name,
          SignalTypes.OperationMode_Eco.name,
          SignalTypes.OperationMode_FrostProtection.name,
          SignalTypes.OperationMode_HeatProtection.name,
          SignalTypes.OperationMode_Night.name,
          SignalTypes.OperationMode_Status.name,
        ];
        if (operationModeTypes.includes(signalType.name)) {
          //
          // für HAVC OperationMode gibt es 2 Möglichkeiten
          // 1. Der Channel wird über den globalen HVAC OperationMode "31/1/20" gesteuert.
          //    das ist der Fall wenn keine Button mit einem HVAC Mode Siganl mit dem Channel verbunden ist
          //
          // 2. Der Channel wird gesteuert übert den HVAC OperationMode des angeschlosssen HeatingButtonChannels
          //    in diesem Fall setzten wir hier "isGlobalGroupAdress" auf false
          //

          const actorChannels = buttonChannel.getConnectedActorChannels();
          if (actorChannels.length > 0) {
            isGlobalGroupAdress = false;
          }
        }

        if (!isGlobalGroupAdress) {
          if (signalType.isStatus && consumerOrMultiplexer.className == "Multiplexer") {
            //
            // Button die mit einem Multiplexer verbunden sind
            //
            // beziehen ihr StatusSignale/StatusGruppen-Addressen NUR vom ERSTEN Consumer,
            // der mit diesem Multiplexer verbunden ist und den gleichen KanalType hat wie der Multiplexer
            //
            // ODER falls es keinen Consumer mit gleichem ChannelTYpe gibt einfach vom ersten Consumer
            //

            const multiplexer = consumerOrMultiplexer as Multiplexer;
            let consumers = multiplexer.getAllConnectedConsumers();
            if (consumers.length > 0) {
              consumers = sortArrayByProperty(consumers, "fullTag");

              let fristConsumer = consumers.find((c) => c.channelType == multiplexer.channelType);
              if (!fristConsumer) {
                fristConsumer = consumers[0];
              }

              let consumerSignal = Consumer.getSignalByType(fristConsumer, signalType);
              if (!consumerSignal && signalType.name == SignalTypes.StatusDisplaySwitch.name) {
                consumerSignal = Consumer.getSignalByType(fristConsumer, SignalTypes.SwitchStatus);
              }

              if (consumerSignal) {
                groupAdress = consumerSignal.groupAddresses.asArray()[0];
              }

              if (groupAdress) {
                //
                // Letztlich nur zur Dokumentation und Nachvollziehbarkeit:
                // Die StatusGruppenAddresse dem Multiplexer hinzufügen
                //
                const multiplexerSignal = multiplexer.getOrCreateSignalByType(signalType);
                multiplexerSignal?.setSingleGroupAddress(groupAdress);
              }
            }
          } else {
            //
            // Hole die GruppenAddesse von angeschlossen Consumer oder Multiplexes
            //
            if (signalType.name == SignalTypes.StatusDisplaySwitch.name) {
              groupAdress = consumerOrMultiplexer?.getGroupAddressByType(SignalTypes.SwitchStatus);
            } else {
              groupAdress = consumerOrMultiplexer?.getGroupAddressByType(signalType);
            }
          }

          if (!groupAdress) {
            logger.error(`linkButtonSingle missing groupAddress for type: ${signalType}`);
          } else {
            // Add Address to Button
            const buttonSignals = buttonChannel.mappedChannel?.getSignalsByType(signalType);
            let found = false;
            buttonSignals?.forEach((buttonSignal) => {
              if (!buttonSignal.isDummySignal && groupAdress) {
                AddressGeneratorUtils.attachGroupAddressToSignal(buttonSignal, groupAdress);
                found = true;
              }
            });

            if (!found) {
              logger.log(`Button: ${buttonChannel.fullTag} has no Signal ${signalType}`);
            }
          }
        }
      });
    }
  } catch (ex) {
    logger.error(ex);
  }
}

function linkGroupButtonStatuses(buttonChannel: ButtonChannel) {
  buttonChannel.assetIsKnxChannel();

  try {
    const consumer = buttonChannel.getConnectedConsumer();
    if (consumer) {
      const signalTypes = getSignalTypeBundle(buttonChannel.channelType);
      signalTypes.forEach((signalType) => {
        if (signalType.name == SignalTypes.StatusDisplaySwitch.name) {
          //
          // mwe 30.4.2025
          // StatusDisplaySwitch muß beim Zuweisen der GruppenAddressen gehandhabt werden wie ein normaler SwitchStatus
          //
          signalType = SignalTypes.SwitchStatus;
        }

        if (signalType.isStatus) {
          const groupAdress = consumer.getGroupAddressByType(signalType);

          if (!groupAdress) {
            logger.error(`missing groupAddress for type: ${signalType}`);
          } else {
            // Add Address to Button
            //
            // MWE 22.4.2025 allow multiple signals of the channel to get this group address
            // ie. two LEDs (left, right) showing the stats of a light controlled by a rocker-button
            //
            const buttonSignals = buttonChannel.requestChannel?.getSignalsByType(signalType);
            buttonSignals?.forEach((buttonSignal) => {
              AddressGeneratorUtils.attachGroupAddressToSignal(buttonSignal, groupAdress);
            });
          }
        }
      });
    }
  } catch (ex) {
    logger.error(ex);
  }
}
